import React, { useRef, useImperativeHandle, forwardRef } from 'react';
import { View, ViewStyle, ScrollView, ScrollViewProps, Platform } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive, getResponsiveValue } from '../../shared/utils/responsive';

// Tab bar heights and safe area to ensure content isn't cut off on mobile
const TAB_BAR_HEIGHT = {
  smallMobile: 50,
  mobile: 56,
  desktop: 64,
};

// Additional bottom padding for safe scrolling on mobile
const MOBILE_SAFE_BOTTOM_PADDING = 32;

export interface PageContainerRef {
  scrollToElement: (elementId: string, offset?: number) => void;
  scrollTo: (y: number, animated?: boolean) => void;
}

interface PageContainerProps {
  children: React.ReactNode;
  scrollable?: boolean;
  contentContainerStyle?: ViewStyle;
  scrollViewProps?: Omit<ScrollViewProps, 'contentContainerStyle' | 'style' | 'children'>;
  maxWidth?: number | 'auto';
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  verticalPadding?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
}

/**
 * Centralized page container component
 * Handles all responsive sizing, padding, and layout consistently
 */
export const PageContainer = forwardRef<PageContainerRef, PageContainerProps>(({
  children,
  scrollable = true,
  contentContainerStyle,
  scrollViewProps,
  maxWidth = 'auto',
  padding = 'lg',
  verticalPadding = '2xl',
}, ref) => {
  const styles = useStyles();
  const { tokens, utils } = styles;
  const responsive = useResponsive();
  const { isMobile, isSmallMobile } = responsive;
  const scrollViewRef = useRef<ScrollView>(null);
  
  // Calculate bottom padding to account for tab bar on mobile + safe area
  const tabBarBottomPadding = Platform.OS === 'web' && isMobile 
    ? (isSmallMobile ? TAB_BAR_HEIGHT.smallMobile : TAB_BAR_HEIGHT.mobile) + MOBILE_SAFE_BOTTOM_PADDING
    : Platform.OS !== 'web' && isMobile
    ? MOBILE_SAFE_BOTTOM_PADDING
    : 0;

  // Expose scroll methods via ref
  useImperativeHandle(ref, () => ({
    scrollToElement: (elementId: string, offset = 100) => {
      if (Platform.OS === 'web') {
        const element = document.getElementById(elementId);
        if (element) {
          const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({ top: elementTop - offset, behavior: 'smooth' });
        }
      } else {
        // Native: measure the element and scroll
        // This is a simplified approach
        scrollViewRef.current?.scrollTo({ y: 0, animated: true });
      }
    },
    scrollTo: (y: number, animated = true) => {
      scrollViewRef.current?.scrollTo({ y, animated });
    },
  }));

  // Responsive max width - matches admin/discussions pattern
  const containerMaxWidth = maxWidth === 'auto' 
    ? getResponsiveValue(500, 600, 700, 800)
    : maxWidth;

  // Responsive horizontal padding
  const containerPadding = padding === 'none' ? 0 : getResponsiveValue(
    padding === 'sm' ? utils.spacing.sm : 
    padding === 'md' ? utils.spacing.md :
    padding === 'lg' ? utils.spacing.lg :
    padding === 'xl' ? utils.spacing.xl :
    utils.spacing.xl, // default lg
    padding === 'sm' ? utils.spacing.md :
    padding === 'md' ? utils.spacing.lg :
    padding === 'lg' ? utils.spacing.lg :
    padding === 'xl' ? utils.spacing.xl :
    utils.spacing.lg,
    padding === 'sm' ? utils.spacing.lg :
    padding === 'md' ? utils.spacing.xl :
    padding === 'lg' ? utils.spacing.xl :
    padding === 'xl' ? utils.spacing.xl :
    utils.spacing.xl,
    padding === 'sm' ? utils.spacing.lg :
    padding === 'md' ? utils.spacing.xl :
    padding === 'lg' ? utils.spacing.xl :
    padding === 'xl' ? utils.spacing.xl :
    utils.spacing.xl
  );

  // Responsive vertical padding
  const verticalPaddingValue = verticalPadding === 'none' ? 0 : getResponsiveValue(
    verticalPadding === 'sm' ? utils.spacing.sm :
    verticalPadding === 'md' ? utils.spacing.md :
    verticalPadding === 'lg' ? utils.spacing.lg :
    verticalPadding === 'xl' ? utils.spacing.xl :
    verticalPadding === '2xl' ? utils.spacing['2xl'] :
    utils.spacing['3xl'],
    verticalPadding === 'sm' ? utils.spacing.md :
    verticalPadding === 'md' ? utils.spacing.lg :
    verticalPadding === 'lg' ? utils.spacing.xl :
    verticalPadding === 'xl' ? utils.spacing['2xl'] :
    verticalPadding === '2xl' ? utils.spacing['2xl'] :
    utils.spacing['3xl'],
    verticalPadding === 'sm' ? utils.spacing.lg :
    verticalPadding === 'md' ? utils.spacing.xl :
    verticalPadding === 'lg' ? utils.spacing['2xl'] :
    verticalPadding === 'xl' ? utils.spacing['2xl'] :
    verticalPadding === '2xl' ? utils.spacing['3xl'] :
    utils.spacing['3xl'],
    verticalPadding === 'sm' ? utils.spacing.lg :
    verticalPadding === 'md' ? utils.spacing.xl :
    verticalPadding === 'lg' ? utils.spacing['2xl'] :
    verticalPadding === 'xl' ? utils.spacing['2xl'] :
    verticalPadding === '2xl' ? utils.spacing['3xl'] :
    utils.spacing['3xl']
  );

  const containerStyle: ViewStyle = {
    flex: 1,
    backgroundColor: tokens.color.background,
    width: '100%',
    maxWidth: '100%',
    ...Platform.select({
      web: {
        minHeight: '100vh',
        overflowX: 'hidden',
      } as any,
    }),
  };

  const innerContainerStyle: ViewStyle = {
    width: '100%',
    maxWidth: containerMaxWidth,
    alignSelf: 'center',
    paddingHorizontal: containerPadding,
    minWidth: 0,
    ...Platform.select({
      web: {
        overflowX: 'hidden',
        boxSizing: 'border-box',
      } as any,
    }),
  };

  if (!scrollable) {
    return (
      <View style={containerStyle}>
        <View style={[innerContainerStyle, { 
          paddingTop: verticalPaddingValue, 
          paddingBottom: verticalPaddingValue + tabBarBottomPadding, 
          flex: 1 
        }, contentContainerStyle]}>
          {children}
        </View>
      </View>
    );
  }

  return (
    <View style={containerStyle}>
      <ScrollView
        ref={scrollViewRef}
        contentContainerStyle={{
          flexGrow: 1,
          paddingTop: verticalPaddingValue,
          paddingBottom: verticalPaddingValue + tabBarBottomPadding,
          ...Platform.select({
            web: {
              overflowX: 'hidden',
            } as any,
          }),
          ...contentContainerStyle,
        }}
        showsVerticalScrollIndicator={false}
        style={{
          width: '100%',
          maxWidth: '100%',
          ...Platform.select({
            web: {
              overflowX: 'hidden',
            } as any,
          }),
        }}
        {...scrollViewProps}
      >
        <View style={innerContainerStyle}>
          {children}
        </View>
      </ScrollView>
    </View>
  );
});

PageContainer.displayName = 'PageContainer';

